import { useMount, useUnmount, useUpdateEffect } from 'ahooks';
import uniqueId from 'lodash-es/uniqueId';
import React from 'react';
import { Box } from '@apia/theme';
import {
  defaultLabels,
  TResponsiveTableContext,
  TResponsiveTableContextProps,
} from './types';
import { responsiveTableActions, responsiveTableStore } from './store';
import { ResponsiveTable } from './ResponsiveTable';
import { getVariant } from '@apia/theme';

export const ResponsiveTableReactContext =
  React.createContext<TResponsiveTableContext>({} as TResponsiveTableContext);

const tableContexts: Record<string, TResponsiveTableContext> = {};

export function useResponsiveTableContext(tableName?: string) {
  const reactContext = React.useContext(ResponsiveTableReactContext);
  const context = tableName ? tableContexts[tableName] : reactContext;
  if (!context) throw new Error('There is no ResponsiveTableContext');
  return context;
}

/**
 * Este contexto busca facilitar la tarea de centralizar las
 * propiedades y callbacks asociados a la tabla, de forma de
 * evitar la necesidad de distribuir comportamientos.
 *
 * **MUY IMPORTANTE**
 * Asegurarse de memoizar las propiedades rows, filters y
 * columns, ya que en caso contrario eso va a disparar
 * actualizaciones en el responsiveTableStore cada vez que el padre del
 * contexto renderice.
 *
 */
const NoMemoResponsiveTableContext = ({
  allowEdition,
  allowRowsKeyboardSorting,
  avoidAutoEllipsis,
  allowSelection,
  allowSorting,
  avoidReparseSelectionOnRowChange,
  children = <ResponsiveTable />,
  className,
  currentBreakPoint,
  customLabels,
  columns,
  filters,
  FiltersRenderer,
  rows,
  label,
  isMultiple,
  name,

  onChangeSelection,
  onFilterBlur,
  onFilterChange,
  onFilterPressEnter,
  onRowClick,
  onSelectRows,
  onSortChange,
  reserveColumnsForStates,
  SelectionHandler,
  variant,
}: TResponsiveTableContextProps) => {
  const actualName = React.useMemo(
    () => name ?? uniqueId('responsiveTable'),
    [name],
  );
  const labels = React.useMemo(
    () => ({ ...defaultLabels, ...customLabels }),
    [customLabels],
  );
  const contextValue = React.useMemo<TResponsiveTableContext>(
    () => ({
      currentBreakPoint,
      label,
      labels,
      avoidAutoEllipsis,
      FiltersRenderer,
      name: actualName,
      onChangeSelection,
      onFilterBlur,
      onFilterChange,
      onFilterPressEnter,
      onRowClick,
      onSelectRows,
      onSortChange,
      SelectionHandler,
    }),
    [
      currentBreakPoint,
      label,
      labels,
      avoidAutoEllipsis,
      actualName,
      FiltersRenderer,
      onChangeSelection,
      onFilterBlur,
      onFilterChange,
      onFilterPressEnter,
      onRowClick,
      onSelectRows,
      onSortChange,
      SelectionHandler,
    ],
  );

  tableContexts[actualName] = contextValue;

  useMount(() => {
    responsiveTableStore.dispatch(
      responsiveTableActions.update({
        allowEdition,
        allowRowsKeyboardSorting,
        allowSelection,
        allowSorting,
        columns,
        filters: filters ?? [],
        rows,
        isMultiple,
        tableName: actualName,
        reserveColumnsForStates,
        handleFocus: !SelectionHandler,
      }),
    );
  });

  useUpdateEffect(() => {
    responsiveTableStore.dispatch(
      responsiveTableActions.update({
        tableName: actualName,
        columns,
        handleFocus: !SelectionHandler,
      }),
    );
  }, [columns]);

  useUpdateEffect(() => {
    responsiveTableStore.dispatch(
      responsiveTableActions.update({
        tableName: actualName,
        rows,
        handleFocus: !SelectionHandler,
      }),
    );
    if (!SelectionHandler && avoidReparseSelectionOnRowChange !== true) {
      const selected = rows
        .map((current, i) => ({ ...current, i }))
        .filter((current) => current.initiallySelected);
      responsiveTableStore.dispatch(
        responsiveTableActions.update({
          tableName: actualName,
          selectedRows: selected.map((current) => current.i),
        }),
      );
    }
  }, [rows]);

  useUpdateEffect(() => {
    responsiveTableStore.dispatch(
      responsiveTableActions.update({
        tableName: actualName,
        filters,
        handleFocus: !SelectionHandler,
      }),
    );
  }, [filters]);

  useUnmount(() => {
    responsiveTableStore.dispatch(
      responsiveTableActions.destroy({ tableName: actualName }),
    );
  });

  return (
    <ResponsiveTableReactContext.Provider value={contextValue}>
      <Box
        id={name}
        className={`responsiveTableContext__wrapper ${className ?? ''}`}
        {...(variant ? getVariant(variant) : null)}
      >
        {children}
      </Box>
    </ResponsiveTableReactContext.Provider>
  );
};

export const ResponsiveTableContext = React.memo(NoMemoResponsiveTableContext);
